import type {
  ModelBankDetails,
  ModelBillingAddress,
  ModelCommunication,
  ModelMerchantAccountSettings,
  ModelMerchantResponse,
  ModelMerchantVoucherSettings,
  ModelNotification,
  ModelNotificationPercentage,
  ModelPaymentPlanSettings,
} from '@layup/api-sdk';

export type OrganisationSettings = {
  name: string;
  tradingName: string;
  vatNo: string;
  registeredEntity: string;
  supportMobile: string;
  supportEmail: string;
  settlementBatchReferencePrefix: string;
  merchantTermsLink: string;
  billingAddress: ModelBillingAddress;
};

// Have to manually create ModelCancellationTermRequest type definition to avoid having to use broken enum
export type OrderSettings = {
  paymentPlans: ModelPaymentPlanSettings;
  voucherSettings: ModelMerchantVoucherSettings;
  pricelistId: string;
  features: ModelMerchantResponse['features'];
  cancellationTerms: CustomCancellationTermRequest;
};

// This type can be removed once the API is fixed to return the correct enum values. cancellationTerms can then be updated to use ModelCancellationPolicyNameEnum
export type CustomCancellationTermRequest = {
  active: boolean;
  definition: Array<{
    type: {
      name: string;
      definition: {
        missed: number;
        percentage: number;
      };
      finalPaymentMissed: boolean;
    };
    policy: {
      name?: string; // Should be ModelCancellationPolicyNameEnum but the values are not correct so we are setting it to string until we can fix all enums
      definition: {
        percentage: number;
        flatFee: number;
        percentageShouldUseTotalPaid: boolean;
      };
    };
    start: number;
    end: number;
    gracePeriod: number;
  }>;
};

export type ClientCommunication = ModelCommunication;

export type NotificationSettings = {
  settlements: ModelNotification;
  invoices: ModelNotification;
  newOrder: ModelNotification;
  amendmentOrder: ModelNotification;
  expiredOrder: ModelNotification;
  suspendedOrder: ModelNotification;
  cancelledOrder: ModelNotification;
  cancellationRequest: ModelNotification;
  completedOrder_75: ModelNotificationPercentage;
  completedOrder: ModelNotification;
  successfulPayment: ModelNotification;
  failedPayment: ModelNotification;
  missedPayment: ModelNotification;
  refunds: ModelNotification;
  transfers: ModelNotification;
};

export type BillingSettings = {
  settlementFrequency: string;
  percFee: number;
  settlementType: string;
  priceList: any;
  accountSettings: ModelMerchantAccountSettings;
  numberOfAddedUsers: number;
  settlementDay: number;
  paymentPlans: ModelMerchantResponse['paymentPlans'];
  features: ModelMerchantResponse['features'];
  accountType: string;
  name: string;
  supportMobile: string;
  registeredEntity: string;
  bankDetails: ModelBankDetails;
};

export type BillingChangeRequest = {
  merchantName: string;
  userName: string;
  userEmail: string;
  merchantId: string;
  merchantContactNumber: string;
  message: string;
  type: string;
};

export type DebitOrderMandateCreateRequest = {
  merchantId: string;
  reference: string;
  holderName: string;
  holderSurname: string;
  contact: string;
  accountType: string;
  accountNumber: string;
  branchCode: string;
  identificationType: string;
  identificationNumber: string;
  bankName: string;
};

export const capitalize = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const BANK_ACCOUNT_TYPES = [
  { value: 'Savings', key: 'Savings' },
  { value: 'Current', key: 'Current' },
  { value: 'Transmission', key: 'Transmission' },
];
